import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import MainLayout from '@shared/ui/main-layout';
import {
  deletePrice,
  fetchAllPrices,
  selectPrices,
  selectPricesStatus,
} from '../../entities/price/model';
import { Can } from '@shared/lib/ability';
import { useAppDispatch, useAppSelector } from '../../app/store/store';
import { LoadingWrapper } from '../../shared/ui/loading-wrapper';
import DataTable, { Column } from '@shared/ui/data-table';
import { ConfirmModal } from '@shared/ui/confirm-modal';
import { DEFAULT_PAGE_SIZE } from '@shared/config/pagination';
import { PriceList } from '../../types/interfaces';

const Prices = () => {
  const prices = useAppSelector(selectPrices);
  const dispatch = useAppDispatch();
  const status = useAppSelector(selectPricesStatus);
  const navigate = useNavigate();

  useEffect(() => {
    if (status !== 'succeeded') {
      dispatch(fetchAllPrices());
    }
  }, [dispatch, status]);

  const remove = (id: number) => {
    dispatch(deletePrice(id));
  };

  const formatDate = (iso: string) => {
    if (!iso) return '—';
    try {
      return new Date(iso).toLocaleDateString('ru-RU', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      });
    } catch {
      return '—';
    }
  };

  const columns: Column<PriceList>[] = [
    {
      key: 'name',
      header: 'Прайс',
      render: (p) => (
        <div className="min-w-0">
          <p className="font-medium text-[#2d2c2c] truncate" title={p.name}>
            {p.name || 'Без названия'}
          </p>
          {p.companies && p.companies.length > 0 ? (
            <p className="text-xs text-gray-400">
              Привязан к {p.companies.length} компании(ям)
            </p>
          ) : (
            <p className="text-xs text-gray-400">Без привязки к компаниям</p>
          )}
        </div>
      ),
    },
    {
      key: 'created',
      header: 'Создан',
      render: (p) => (
        <span className="text-gray-600 text-xs">
          {formatDate(p.created_at)}
        </span>
      ),
    },
    {
      key: 'updated',
      header: 'Обновлён',
      render: (p) => (
        <span className="text-gray-600 text-xs">
          {formatDate(p.updated_at)}
        </span>
      ),
    },
    {
      key: 'actions',
      header: '',
      stopRowClick: true,
      headerClassName: 'w-[1%]',
      className: 'text-right whitespace-nowrap',
      render: (p) => (
        <div className="flex items-center justify-end gap-3">
          <Can I="change" a="pricelist">
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/prices/edit/${p.id}`);
              }}
              className="text-sm font-medium text-[#e3282a] hover:text-[#c82022] transition-colors"
            >
              Изменить
            </button>
          </Can>
          <Can I="delete" a="pricelist">
            <ConfirmModal
              danger
              openButton={
                <button
                  type="button"
                  className="text-sm font-medium text-gray-500 hover:text-[#e3282a] transition-colors"
                >
                  Удалить
                </button>
              }
              confirm={() => remove(p.id)}
              texts={{
                title: 'Удаление прайса',
                text: `Удалить прайс «${p.name || 'без названия'}»? Связи с компаниями тоже будут сняты.`,
                cancel: 'Отмена',
                confirm: 'Удалить',
              }}
            />
          </Can>
        </div>
      ),
    },
  ];

  return (
    <LoadingWrapper status={status}>
      <MainLayout>
        <MainLayout.Title>Прайсы</MainLayout.Title>
        <Can I="add" a="pricelist">
          <MainLayout.AddButton
            text={'прайс'}
            visible={true}
            path={'/prices/add'}
          />
        </Can>
        <MainLayout.Main>
          <div className="bg-white rounded-xl shadow-sm overflow-hidden">
            <div className="px-6 py-4">
              <DataTable<PriceList>
                columns={columns}
                data={prices}
                keyAccessor={(p) => p.id}
                onRowClick={(p) => navigate(`/prices/${p.id}`)}
                loading={status === 'loading' && prices.length === 0}
                emptyMessage="Прайсов пока нет"
                pageSize={DEFAULT_PAGE_SIZE}
              />
            </div>
          </div>
        </MainLayout.Main>
      </MainLayout>
    </LoadingWrapper>
  );
};

export default Prices;
